import { useEffect, useSyncExternalStore } from 'react'
import { Navigate, Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { ArmazonConsola } from '@/consola/ArmazonConsola'
import { sesionConsolaActual, suscribirseConsola } from '@/dominio/consola/sesion'
import { clavesConsola } from '@/dominio/consola/useConsola'

/**
 * La consola del dueño (`docs/09-consola-del-dueno.md` §6).
 *
 * Todo lo que cuelga de `/consola` pasa por aquí, y aquí se decide lo primero:
 * sin sesión **de la consola** se va a la entrada. La del portal no cuenta: son
 * dos testigos con dos audiencias y el uno no abre lo del otro.
 *
 * Si mañana la consola se despliega aparte, se muda esta carpeta con
 * `consola/` y `dominio/consola/`, y nada del portal depende de ellas salvo la
 * franja de soporte.
 */
function esEntrada(ruta: string): boolean {
  return ruta === '/consola/entrar' || ruta.startsWith('/consola/entrar/')
}

export const Route = createFileRoute('/consola')({
  // La comprobación va **antes de cargar**, no solo al pintar. Con la división
  // de código por ruta, la pantalla hija llegaba a montarse y a pedir
  // `/consola/tenants` sin testigo antes de que el armazón redirigiera: un 401
  // inútil en el mejor caso, y datos de todos los clientes pedidos sin sesión
  // como costumbre en el peor.
  beforeLoad: ({ location }) => {
    if (!esEntrada(location.pathname) && sesionConsolaActual() === null) {
      throw redirect({ to: '/consola/entrar', search: { volver: location.pathname }, replace: true })
    }
  },
  component: RaizConsola,
})

function RaizConsola() {
  const sesion = useSyncExternalStore(suscribirseConsola, sesionConsolaActual, () => null)
  const ruta = useRouterState({ select: (estado) => estado.location.pathname })
  const cliente = useQueryClient()
  const enEntrada = esEntrada(ruta)

  // Al caducar o salir, lo cacheado era de todos los contribuyentes. No se
  // deja en memoria para la siguiente persona que se siente.
  useEffect(() => {
    if (sesion === null) cliente.removeQueries({ queryKey: clavesConsola.todo })
  }, [sesion, cliente])

  if (enEntrada) return <Outlet />

  // La sesión puede caducar con la pantalla abierta (30 minutos, o un 401):
  // entonces no hay navegación que dispare `beforeLoad`, y se sale desde aquí.
  if (sesion === null) {
    return <Navigate to="/consola/entrar" search={{ volver: ruta }} replace />
  }

  return (
    <ArmazonConsola sesion={sesion}>
      <Outlet />
    </ArmazonConsola>
  )
}
